// src/context/DashboardContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import { message } from 'antd';

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [updateSource, setUpdateSource] = useState(null);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState([]);

  // Fonction pour rafraîchir le dashboard
  const refreshDashboard = useCallback((source = 'manual') => {
    setRefreshKey(prev => prev + 1);
    setLastUpdated(new Date());
    setUpdateSource(source);
    
    // Afficher une notification silencieuse pour les mises à jour automatiques
    if (source !== 'manual' && source !== 'initial') {
      message.info({
        content: `Dashboard mis à jour (${source})`,
        duration: 2,
        style: { marginTop: '50px' }
      });
    }
  }, []);

  // Fonction pour ajouter une mise à jour en attente
  const addPendingUpdate = useCallback((update) => {
    setPendingUpdates(prev => [...prev, { ...update, timestamp: new Date() }]);
  }, []);

  // Effacer les mises à jour en attente
  const clearPendingUpdates = useCallback(() => {
    setPendingUpdates([]);
  }, []);

  // Configurer WebSocket
  const setupWebSocket = useCallback(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_WS_URL || `${wsProtocol}//${window.location.host}`;
    const ws = new WebSocket(`${wsHost}/ws/dashboard`);

    ws.onopen = () => {
      console.log('✅ WebSocket connecté');
      setIsWebSocketConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📩 Mise à jour WebSocket reçue:', data);
        
        // Ajouter à la file d'attente
        addPendingUpdate(data);
        
        // Rafraîchir le dashboard avec la source
        refreshDashboard(data.source || 'websocket');
      } catch (error) {
        console.error('❌ Erreur WebSocket:', error);
      }
    };

    ws.onclose = () => {
      console.log('🔌 WebSocket déconnecté');
      setIsWebSocketConnected(false);
      
      // Reconnexion après 5 secondes
      setTimeout(() => {
        if (ws.readyState === WebSocket.CLOSED) {
          setupWebSocket();
        }
      }, 5000);
    };

    ws.onerror = (error) => {
      console.error('❌ Erreur WebSocket:', error);
      setIsWebSocketConnected(false);
    };

    return ws;
  }, [refreshDashboard, addPendingUpdate]);

  // Initialiser WebSocket
  React.useEffect(() => {
    const ws = setupWebSocket();
    return () => {
      if (ws) ws.close();
    };
  }, [setupWebSocket]);

  // Valeur du contexte
  const value = {
    refreshKey,
    lastUpdated,
    updateSource,
    isWebSocketConnected,
    pendingUpdates,
    refreshDashboard,
    addPendingUpdate,
    clearPendingUpdates
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};